import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from '@/components/layout/app-shell/app-shell';
import { AnalyticsPage } from '@/pages/analytics/analytics';
import { AssistantPage } from '@/pages/assistant/assistant';
import { ClassroomPage } from '@/pages/classroom/classroom';
import { DashboardPage } from '@/pages/dashboard/dashboard';
import { FeedbackPage } from '@/pages/feedback/feedback';
import { CreateJobPage } from '@/pages/jobs/create-job';
import { JobDetailPage } from '@/pages/jobs/job-detail';
import { JobsPage } from '@/pages/jobs/jobs';
import { SubmitPage } from '@/pages/jobs/submit';
import { LoginPage } from '@/pages/login/login';
import { NotificationsPage } from '@/pages/notifications/notifications';
import { JoinClassPage } from '@/pages/student/join-class';
import { MistakesPage } from '@/pages/student/mistakes';
import { MyClassesPage } from '@/pages/student/my-classes';
import { ReportPage } from '@/pages/student/report';
import { ReviewDetailPage } from '@/pages/review/review-detail';
import { ReviewQueuePage } from '@/pages/review/review-queue';
import { SettingsPage } from '@/pages/settings/settings';
import { TodoPage } from '@/pages/todo/todo';
import { useAuthStore } from '@/stores/authStore';
import { UserRole } from '@/types';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? <Navigate to="/" replace /> : children;
}

function TeacherRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" replace />;
  return user.role === UserRole.TEACHER ? children : <Navigate to="/" replace />;
}

function StudentRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" replace />;
  return user.role === UserRole.STUDENT ? children : <Navigate to="/" replace />;
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="jobs" element={<JobsPage />} />
        <Route
          path="jobs/create"
          element={
            <TeacherRoute>
              <CreateJobPage />
            </TeacherRoute>
          }
        />
        <Route path="jobs/:id" element={<JobDetailPage />} />
        <Route
          path="jobs/:id/edit"
          element={
            <TeacherRoute>
              <CreateJobPage />
            </TeacherRoute>
          }
        />

        <Route
          path="jobs/:id/submit"
          element={
            <StudentRoute>
              <SubmitPage />
            </StudentRoute>
          }
        />
        <Route path="review/:submissionId" element={<ReviewDetailPage />} />
        <Route
          path="review/queue"
          element={
            <TeacherRoute>
              <ReviewQueuePage />
            </TeacherRoute>
          }
        />
        <Route
          path="class/analytics"
          element={
            <TeacherRoute>
              <AnalyticsPage />
            </TeacherRoute>
          }
        />
        <Route
          path="classroom"
          element={
            <TeacherRoute>
              <ClassroomPage />
            </TeacherRoute>
          }
        />
        <Route
          path="join"
          element={
            <StudentRoute>
              <JoinClassPage />
            </StudentRoute>
          }
        />
        <Route
          path="my-classes"
          element={
            <StudentRoute>
              <MyClassesPage />
            </StudentRoute>
          }
        />
        <Route
          path="mistakes"
          element={
            <StudentRoute>
              <MistakesPage />
            </StudentRoute>
          }
        />
        <Route
          path="report"
          element={
            <StudentRoute>
              <ReportPage />
            </StudentRoute>
          }
        />
        <Route
          path="todo"
          element={
            <StudentRoute>
              <TodoPage />
            </StudentRoute>
          }
        />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="feedback" element={<FeedbackPage />} />
        <Route path="assistant" element={<AssistantPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
    </Routes>
  );
}
